import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { State } from 'ts-fsrs';
import { supabase } from '../lib/supabase.js';
import { uuid } from '../lib/uuid.js';
import { useAuth } from '../auth/authStore.js';
import {
  applySessionResultToCaches,
  invalidateProgress,
  useCompletions,
  type SessionCompletionResult,
} from '../progress/hooks.js';
import {
  buildSessionPlan,
  countIntroducedToday,
  extractDeckVocab,
  type CardStateRow,
  type DeckEntry,
  type SessionPlan,
} from './deck.js';
import type { EmittedReview } from './sessionReducer.js';
import { emittedToRow } from './sessionReducer.js';
import { usePendingReviewStore, type PendingReview } from './pendingReviewStore.js';

/**
 * Flashcards data layer. Reads follow the progress-hooks pattern (React
 * Query over Supabase, per-user keys). Writes are queue-first: every grade
 * lands in the pending store and the flusher batches it to the
 * `apply_flashcard_reviews` RPC (replay-idempotent per client_id), with the
 * states cache updated optimistically at grade time — no spinners, no lost
 * reviews on flaky network.
 */

export const CARD_STATES_KEY = (userId: string) => ['flashcards', 'states', userId] as const;

interface CardStateApiRow {
  card_key: string;
  direction: number;
  due: string;
  stability: number;
  difficulty: number;
  scheduled_days: number;
  learning_steps: number;
  reps: number;
  lapses: number;
  state: number;
  last_review: string | null;
  suspended: boolean;
  created_at: string;
}

function parseRow(row: CardStateApiRow): CardStateRow {
  return {
    card_key: row.card_key,
    direction: row.direction,
    due: new Date(row.due),
    stability: row.stability,
    difficulty: row.difficulty,
    scheduled_days: row.scheduled_days,
    learning_steps: row.learning_steps,
    reps: row.reps,
    lapses: row.lapses,
    state: row.state as State,
    last_review: row.last_review ? new Date(row.last_review) : null,
    suspended: row.suspended,
    created_at: new Date(row.created_at),
  };
}

async function fetchCardStates(userId: string): Promise<Map<string, CardStateRow>> {
  const { data, error } = await supabase
    .from('user_flashcards')
    .select(
      'card_key, direction, due, stability, difficulty, scheduled_days, learning_steps, reps, lapses, state, last_review, suspended, created_at',
    )
    .eq('user_id', userId);
  if (error) throw error;
  const map = new Map<string, CardStateRow>();
  for (const row of data ?? []) map.set(row.card_key, parseRow(row));
  return map;
}

export function useCardStates() {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? CARD_STATES_KEY(user.id) : ['flashcards', 'states', 'anon'],
    queryFn: () => fetchCardStates(user!.id),
    enabled: !!user,
  });
}

/** Deck entries derived from completed lessons (bundled content, no network). */
export function useDeckEntries(): { entries: DeckEntry[]; loaded: boolean } {
  const { data: completions = {}, isPlaceholderData } = useCompletions();
  const entries = useMemo(() => extractDeckVocab(Object.keys(completions)), [completions]);
  return { entries, loaded: !isPlaceholderData };
}

export interface DeckOverview {
  plan: SessionPlan;
  entries: DeckEntry[];
  states: Map<string, CardStateRow>;
  newPerDay: number;
  /** True once both completions and card states have genuinely resolved. */
  loaded: boolean;
}

/** Today's session plan + deck stats for the flashcards home screen. */
export function useDeckOverview(now: Date = new Date()): DeckOverview {
  const { profile } = useAuth();
  const { entries, loaded: entriesLoaded } = useDeckEntries();
  const { data: states, isSuccess: statesLoaded } = useCardStates();

  const timezone = profile?.timezone ?? 'Asia/Tbilisi';
  const newPerDay = profile?.flashcards_new_per_day ?? 10;

  const plan = useMemo(() => {
    const stateMap = states ?? new Map<string, CardStateRow>();
    const introducedToday = countIntroducedToday(stateMap.values(), now, timezone);
    return buildSessionPlan(entries, stateMap, { now, newPerDay, introducedToday });
    // `now` is intentionally captured per render of the consumer.
  }, [entries, states, newPerDay, timezone, now]);

  return {
    plan,
    entries,
    states: states ?? new Map(),
    newPerDay,
    loaded: entriesLoaded && statesLoaded,
  };
}

/* ------------------- review persistence (queue-first) ------------------- */

/**
 * Persist a batch of graded reviews: optimistic cache update + enqueue for
 * the flusher. Called per grade from the session player.
 */
export function useRecordReviews() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const enqueue = usePendingReviewStore((s) => s.enqueue);

  return (reviews: EmittedReview[]) => {
    if (!user || reviews.length === 0) return;

    // Optimistic: fold each review into the cached card-state map.
    qc.setQueryData<Map<string, CardStateRow>>(CARD_STATES_KEY(user.id), (prev) => {
      const next = new Map(prev ?? []);
      for (const r of reviews) next.set(r.card_key, emittedToRow(r, next.get(r.card_key)));
      return next;
    });

    const pending: PendingReview[] = reviews.map((r) => ({
      ...r,
      client_id: uuid(),
      userId: user.id,
      queuedAt: Date.now(),
    }));
    enqueue('reviews', pending);
  };
}

/** Direct RPC used by the flusher. Batches are replay-safe per client_id. */
export async function applyReviewsDirect(reviews: PendingReview[]): Promise<void> {
  const payload = reviews.map((r) => ({
    client_id: r.client_id,
    card_key: r.card_key,
    direction: r.direction,
    rating: r.rating,
    reviewed_at: r.reviewed_at,
    card: r.card,
    log: r.log,
  }));
  const { error } = await supabase.rpc('apply_flashcard_reviews', { p_reviews: payload });
  if (error) throw error;
}

/* ------------------- session completion (XP + streak) ------------------- */

/** @deprecated alias — the shared shape lives in progress/hooks.ts. */
export type FlashcardSessionResult = SessionCompletionResult;

export interface CompleteSessionPayload {
  sessionId: string;
  cards: number;
}

export async function completeSessionDirect(
  payload: CompleteSessionPayload,
): Promise<FlashcardSessionResult> {
  const { data, error } = await supabase.rpc('complete_flashcard_session', {
    p_session_id: payload.sessionId,
    p_cards: payload.cards,
  });
  if (error) throw error;
  return data as unknown as FlashcardSessionResult;
}

/**
 * Award XP + streak for a finished session — QUEUE-FIRST: the session is
 * enqueued before the RPC attempt, so a tab close / crash mid-flight can
 * never lose it (the flusher picks it up next launch). Safe to double-fire:
 * the RPC is idempotent per session_id, a concurrent replay awards nothing.
 */
export function useCompleteFlashcardSession() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const enqueue = usePendingReviewStore((s) => s.enqueue);
  const remove = usePendingReviewStore((s) => s.remove);

  return useMutation<FlashcardSessionResult, Error, CompleteSessionPayload>({
    mutationFn: completeSessionDirect,

    onMutate: (payload) => {
      if (!user) return;
      enqueue('sessions', [
        {
          session_id: payload.sessionId,
          cards: payload.cards,
          userId: user.id,
          queuedAt: Date.now(),
        },
      ]);
    },

    onSuccess: (result, payload) => {
      if (!user) return;
      remove('sessions', [payload.sessionId]);
      applySessionResultToCaches(qc, user.id, result);
      // The result carries canonical streak/XP but says nothing about the
      // reviews the RPC just counted — missions (cards/day, sessions/week)
      // and the rest of the progress reads have to come from the server.
      // Same call the FlashcardFlusher makes after a queued replay.
      invalidateProgress(qc, user.id);
    },

    // On error the queued entry simply stays for the flusher.
  });
}
